import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import ListBlankState from '@components/ListBlankState'
import PageHeader from '@components/PageHeader'

import { groupEntitiesByEdition } from '@functions/publicationEdition'
import { sanitiseObject } from '@functions/utility'

import useCurrentUser from '@hooks/useCurrentUser'
import usePublicationBookings from '@hooks/usePublicationBookings'
import useSelector from '@hooks/useSelector'
import { generateUrls } from '@hooks/usePublicationBooking'

import NewsDeskFilters from '@sections/Admin/packs/NewsDesk/components/NewsDeskFilters'

import PublicationEditionGroup from '../PublicationEditionGroup'

const allowedFilters = ['booking_status', 'brand_id', 'publication_id', 'publication_edition_id', 'string']

const bookingStatuses = [
  { key: 'approved', title: 'Approved' },
  { key: 'booked', title: 'Booked' },
  { key: 'current', title: 'Current' },
  { key: 'ftp_transfer_successful', title: 'FTP Unsynced' },
  { key: 'pending', title: 'Pending' },
]

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { isAdmin } = useCurrentUser()

  const sanitisedFilters = sanitiseObject(pageFilters, allowedFilters)

  const publicationBookingsPayload = usePublicationBookings({
    filters: {
      ...sanitisedFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      include_publication_booking_artwork_count: true,
      include_publication_booking_brand_group: true,
      include_publication_booking_item_campaign: true,
      include_publication_booking_items: true,
      include_publication_booking_publication: true,
      include_publication_booking_publication_edition: true,
      include_publication_booking_publication_order: true,
      include_publication_booking_publication_product: true,
      include_publication_edition_deadlines: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationBookings,
    filteredPublicationBookingsCount,
    loading,
  } = publicationBookingsPayload

  const publicationEditions = useSelector(reduxState => reduxState.entities.publicationEditions)

  const groupedOrdersByEdition = groupEntitiesByEdition({
    callbacks: {
      getPrimaryGroupId: entity => entity.publication_edition_id,
      getSecondaryGroupId: entity => entity.brand_group_id,
    },
    entities: filteredPublicationBookings,
    publicationEditions,
    secondaryGroupKey: 'brandGroups',
  })

  const hasGroupedOrdersByEdition = !!groupedOrdersByEdition.length

  const { bookingsUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Bookings | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="bookings"
        nestedNavigation={isAdmin}
        tabBarItems={[
          {
            href: bookingsUrl,
            icon: faHome,
            key: 'bookings',
            title: 'bookings',
          },
        ]}
        title="Bookings"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasGroupedOrdersByEdition && <ListBlankState />}

            {!loading && hasGroupedOrdersByEdition && (
              <Box flexDirection="column">
                {Object.values(groupedOrdersByEdition).map(group => (
                  <PublicationEditionGroup
                    key={group.id}
                    editionBrandGroups={group.brandGroups}
                    orderCount={group.entityCount}
                    publicationEditionId={group.id}
                  />
                ))}

                <Box variant="white">
                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredPublicationBookingsCount}
                    loading={loading}
                    showBorderTop={false}
                  />
                </Box>
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NewsDeskFilters
              allowedFilters={allowedFilters}
              pageFilters={pageFilters}
              // @ts-expect-error TS2322
              statuses={bookingStatuses}
              strings={{
                stringLabel: 'Filter by ID, Confirmation ID or Address',
              }}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
